import { useState } from 'react';
import { Award, ChevronDown, ChevronUp } from 'lucide-react';
import type { AwardReveal, EventAwards } from '../../services/eventAwards';

interface EventAwardsCardProps {
  awards: EventAwards;
  attendeeId?: string;
  collapsible?: boolean;
}

function comingUp(awards: EventAwards): { names: Array<{ name: string; emoji: string }>; hiddenCount: number } {
  if (awards.status === 'done') return { names: [], hiddenCount: 0 };
  if (awards.status === 'announced') return { names: awards.upcoming, hiddenCount: 0 };
  if (awards.upcoming.length > 0) return { names: awards.upcoming.slice(awards.revealed.length), hiddenCount: 0 };
  return { names: [], hiddenCount: Math.max(0, awards.totalCount - awards.revealed.length) };
}

function expandedKey(eventId: string): string {
  return `votebeats_awards_expanded_${eventId}`;
}

function readExpanded(eventId: string): boolean {
  try {
    return localStorage.getItem(expandedKey(eventId)) === '1';
  } catch (error) {
    console.warn('Could not read whether the awards card was expanded:', error);
    return false;
  }
}

function rememberExpanded(eventId: string, expanded: boolean): void {
  try {
    if (expanded) localStorage.setItem(expandedKey(eventId), '1');
    else localStorage.removeItem(expandedKey(eventId));
  } catch (error) {
    console.warn('Could not remember whether the awards card was expanded:', error);
  }
}

function AwardRow({ reveal, isMe, open, onToggle }: { reveal: AwardReveal; isMe: boolean; open: boolean; onToggle: () => void }) {
  const content = (
    <>
      <span className="text-2xl leading-none" aria-hidden="true">{reveal.emoji}</span>
      <span className="min-w-0 flex-1 block">
        <span className="block text-sm font-semibold text-gray-900 dark:text-white">{reveal.name}</span>
        <span className="block text-sm text-gray-700 dark:text-gray-300 truncate">
          {reveal.winnerName}
          {isMe && (
            <span className="ml-2 inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase rounded bg-amber-500 text-white">You</span>
          )}
        </span>
        {reveal.stat && <span className="block text-xs text-gray-500 dark:text-gray-400">{reveal.stat}</span>}
        {open && reveal.howWon && (
          <span className="block mt-1 text-xs text-amber-700 dark:text-amber-300">How it's won: {reveal.howWon}</span>
        )}
      </span>
    </>
  );
  const rowClass = `w-full text-left flex items-start gap-3 px-3 py-2 rounded-lg ${
    isMe ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700' : 'bg-gray-50 dark:bg-gray-800/60'
  }`;
  if (!reveal.howWon) return <li className={rowClass}>{content}</li>;
  return (
    <li>
      <button type="button" onClick={onToggle} aria-expanded={open} title={reveal.howWon} className={rowClass}>
        {content}
      </button>
    </li>
  );
}

export default function EventAwardsCard({ awards, attendeeId, collapsible = false }: EventAwardsCardProps) {
  const [expanded, setExpanded] = useState(() => readExpanded(awards.eventId));
  const [openCategoryId, setOpenCategoryId] = useState<string | null>(null);
  const { names, hiddenCount } = comingUp(awards);
  if (awards.revealed.length === 0 && names.length === 0 && hiddenCount === 0) return null;

  const canCollapse = collapsible && awards.status === 'done' && awards.revealed.length > 0;
  const collapsed = canCollapse && !expanded;
  const isMine = (reveal: AwardReveal) => Boolean(attendeeId) && reveal.winnerId === attendeeId;
  const shown = collapsed ? awards.revealed.filter(isMine) : awards.revealed;

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    rememberExpanded(awards.eventId, next);
  };

  return (
    <section
      aria-label="Tonight's awards"
      className="bg-white dark:bg-gray-900 border border-amber-200 dark:border-gray-600 rounded-xl p-4 space-y-3"
    >
      <h2 className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
        <Award className="w-5 h-5 text-amber-500" />
        Tonight's awards
      </h2>

      {shown.length > 0 && (
        <ul className="space-y-2">
          {shown.map((reveal) => (
            <AwardRow
              key={reveal.categoryId}
              reveal={reveal}
              isMe={isMine(reveal)}
              open={openCategoryId === reveal.categoryId}
              onToggle={() => setOpenCategoryId((current) => current === reveal.categoryId ? null : reveal.categoryId)}
            />
          ))}
        </ul>
      )}

      {canCollapse && (
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={!collapsed}
          className="flex items-center gap-1 text-sm font-medium text-amber-700 dark:text-amber-300 hover:underline"
        >
          {collapsed ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          {collapsed ? `Show all ${awards.revealed.length} awards` : 'Show less'}
        </button>
      )}

      {names.length > 0 && (
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Coming up</p>
          <ul className="flex flex-wrap gap-2">
            {names.map((item, index) => (
              <li
                key={`${item.name}-${index}`}
                className="px-2 py-1 text-sm rounded-full bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400"
              >
                {item.emoji} {item.name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {hiddenCount > 0 && (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {hiddenCount} more to come
        </p>
      )}
    </section>
  );
}
